import intersection from 'lodash/intersection';
import {
  MuscleGroup,
  coreMuscles,
  backMuscles,
  chestMuscles,
  armMuscles,
  legMuscles,
  shoulderMuscles,
  SetType,
  muscleGroups,
  SimplifiedMuscleGroup,
} from '.';

interface SetCountableSet {
  indicator: SetType;
}

interface SetCountableExercise {
  muscle_group: MuscleGroup;
  other_muscles: MuscleGroup[];
  sets: SetCountableSet[];
}

export interface SetCountableWorkoutOrRoutine {
  exercises: SetCountableExercise[];
}

export const calculateSetContributionsForSimplifiedMuscleGroups = ({
  muscle_group,
  other_muscles,
}: {
  muscle_group: MuscleGroup;
  other_muscles: MuscleGroup[];
}) => {
  const getContribution = (mg: readonly MuscleGroup[]) => {
    if (mg.includes(muscle_group)) {
      return 1;
    } else if (intersection(mg, other_muscles).length) {
      return 0.5;
    } else if (muscle_group === 'full_body') {
      return 1 / 6;
    }
    return 0;
  };

  return {
    core: getContribution(coreMuscles),
    shoulders: getContribution(shoulderMuscles),
    arms: getContribution(armMuscles),
    legs: getContribution(legMuscles),
    back: getContribution(backMuscles),
    chest: getContribution(chestMuscles),
  };
};

export const calculateSimplifiedMuscleSplit = ({
  workoutsOrRoutines,
  includeWarmupSets,
}: {
  workoutsOrRoutines: SetCountableWorkoutOrRoutine[];
  includeWarmupSets: boolean;
}): Record<SimplifiedMuscleGroup, number> => {
  const totals = {
    core: 0,
    shoulders: 0,
    arms: 0,
    legs: 0,
    back: 0,
    chest: 0,
  };

  for (const workoutOrRoutine of workoutsOrRoutines) {
    for (const exercise of workoutOrRoutine.exercises) {
      const trimmedSets = includeWarmupSets
        ? exercise.sets
        : exercise.sets.filter(s => s.indicator !== 'warmup');

      const setCount = trimmedSets.length;

      const contributions = calculateSetContributionsForSimplifiedMuscleGroups({
        muscle_group: exercise.muscle_group,
        other_muscles: exercise.other_muscles,
      });

      totals.core += contributions.core * setCount;
      totals.shoulders += contributions.shoulders * setCount;
      totals.arms += contributions.arms * setCount;
      totals.legs += contributions.legs * setCount;
      totals.back += contributions.back * setCount;
      totals.chest += contributions.chest * setCount;
    }
  }

  return totals;
};

export const calculateSetContributionsForMuscleGroups = ({
  muscleGroup,
  otherMuscles,
}: {
  muscleGroup: MuscleGroup;
  otherMuscles: MuscleGroup[];
}): Record<MuscleGroup, number> => {
  const getContribution = (mg: MuscleGroup) => {
    if (mg === muscleGroup) {
      return 1;
    } else if (otherMuscles.includes(mg)) {
      return 0.5;
    }
    return 0;
  };

  return muscleGroups.reduce(
    (acc, mg) => {
      acc[mg] = getContribution(mg);
      return acc;
    },
    {} as Record<MuscleGroup, number>,
  );
};

export const calculateSetCountPerMuscleGroups = ({
  workoutsOrRoutines,
  includeWarmupSets,
}: {
  workoutsOrRoutines: SetCountableWorkoutOrRoutine[];
  includeWarmupSets: boolean;
}): Record<MuscleGroup, number> => {
  const totals = muscleGroups.reduce(
    (acc, muscleGroup) => {
      acc[muscleGroup] = 0;
      return acc;
    },
    {} as Record<MuscleGroup, number>,
  );

  for (const workoutOrRoutine of workoutsOrRoutines) {
    for (const exercise of workoutOrRoutine.exercises) {
      const trimmedSets = includeWarmupSets
        ? exercise.sets
        : exercise.sets.filter(s => s.indicator !== 'warmup');
      const setCount = trimmedSets.length;

      const contributions = calculateSetContributionsForMuscleGroups({
        muscleGroup: exercise.muscle_group,
        otherMuscles: exercise.other_muscles,
      });

      for (const muscleGroup of muscleGroups) {
        totals[muscleGroup] += contributions[muscleGroup] * setCount;
      }
    }
  }

  return totals;
};
